<script setup lang="ts">
/**
 * Shelf handles on the model (plan §6 shape rules): role="slider" per shelf, drag previews live
 * and commits on release, ArrowUp/Down 1 mm, PageUp/Down 10 mm (Shift ×10), with a spoken value
 * text. Compact mode keeps a 44 px hit area with a quiet grip.
 */
import { computed, markRaw, onBeforeUnmount, onMounted, ref } from 'vue';
import type { BuildModel, Design } from '@/domain';
import { classifyChange, describeChange, fmtMm, limitsOf, mm, setShelfDatum } from '@/domain';
import { familyOf } from '@/families/registry';
import Icon from './Icon.vue';
import { app, snap, stagePreview, ui, viewerRef } from '@/app/state';
import { announce } from '@/app/announce';

const props = defineProps<{ stage: HTMLElement | null }>();
const emit = defineEmits<{ dragging: [boolean] }>();

const wrap = ref<HTMLElement | null>(null);
const els: HTMLElement[] = [];
function setEl(el: unknown, i: number) { if (el) els[i] = el as HTMLElement; }
const build = computed<BuildModel>(() => stagePreview.value?.build ?? snap.value.build);
const minOpeningMm = computed(() => limitsOf(snap.value.construction).minOpening / 10);
/** The bay whose shelves the handles move (Shape's bay selector). */
const bay = computed(() => Math.min(ui.bay, build.value.bays.length - 1));
const multi = computed(() => build.value.bays.length > 1);

interface HandleInfo { k: number; partId: string; label: string; min: number; max: number; now: number; text: string; value: string }
/** One slider per handle the family declares (BoardAssembly.handles) in the selected bay. */
const info = computed<HandleInfo[]>(() => {
  const b = build.value;
  const out: HandleInfo[] = [];
  const mine = b.openings.filter((o) => (o.bay ?? 0) === bay.value);
  for (const h of b.handles.filter((x) => (x.bay ?? 0) === bay.value && x.index !== undefined)) {
    const k = h.index!;
    const part = b.parts.find((p) => p.id === h.partId);
    const below = mine[k - 1], above = mine[k];
    if (!part || part.datum == null || !below || !above) continue;
    const lo = below.bottomDatum + minOpeningMm.value;
    const hi = above.topDatum - minOpeningMm.value - b.thickness.carcass;
    out.push({
      k, partId: h.partId, label: multi.value ? `Bay ${bay.value + 1} shelf ${k} height` : `Shelf ${k} height`, min: lo, max: Math.round(hi * 10) / 10, now: part.datum,
      text: `${fmtMm(mm(part.datum))} mm from the floor. Opening ${k} below is ${fmtMm(below.clear)} mm, opening ${k + 1} above is ${fmtMm(above.clear)} mm.`,
      value: fmtMm(mm(part.datum)),
    });
  }
  return out;
});
const partIdOf = (k: number) => info.value.find((x) => x.k === k)?.partId ?? `shelf-${k}`;

// ---------------------------------------------------------------------------------------------
// Preview and commit
// ---------------------------------------------------------------------------------------------
function previewDesign(design: Design): BuildModel {
  const b = markRaw(familyOf(design).generate(design, app.store.stock, app.store.construction));
  stagePreview.value = { design, build: b };
  viewerRef.value?.update(b, { animate: false });
  return b;
}
function commitPreview(label: string) {
  const p = stagePreview.value;
  stagePreview.value = null;
  if (!p) return;
  if (classifyChange(app.store.design, p.design, app.store.stock, app.store.construction) === 'none') {
    viewerRef.value?.update(app.store.build, { animate: false });
    return;
  }
  const ch = describeChange(app.store.design, p.design, app.store.stock, app.store.construction);
  app.store.apply(() => p.design, { label });
  viewerRef.value?.update(app.store.build, { animate: false });
  announce(`${ch[0] || label}. Openings ${p.build.openings.map((o) => fmtMm(o.clear)).join(', ')} mm.`);
}

// ---------------------------------------------------------------------------------------------
// Placement (every rendered frame)
// ---------------------------------------------------------------------------------------------
function placeHandles() {
  const viewer = viewerRef.value;
  const hs = els.slice(0, info.value.length).filter(Boolean);
  if (!viewer || !hs.length) return;
  const b = build.value;
  const host = wrap.value?.parentElement;
  const w0 = host?.clientWidth ?? 0;
  const xRight = (() => { const o = b.openings.find((x) => (x.bay ?? 0) === bay.value); return o ? o.bounds.x1 + b.thickness.carcass : b.envelope.W; })();
  const pts = hs.map((_h, i) => {
    const part = b.parts.find((p) => p.id === info.value[i]?.partId);
    return part ? viewer.getScreenPosition({ point: { x: xRight, y: part.placement.y + part.placement.sy / 2, z: b.envelope.D } }) : null;
  });
  const bottomPart = b.parts.find((p) => p.role === 'bottom');
  const top = viewer.getScreenPosition({ point: { x: xRight, y: b.envelope.H - b.thickness.carcass / 2, z: b.envelope.D } });
  const bot = viewer.getScreenPosition({ point: { x: xRight, y: (bottomPart?.placement.y ?? 0) + b.thickness.carcass / 2, z: b.envelope.D } });
  const ys = [bot?.y, ...pts.map((p) => p?.y), top?.y];
  let minGap = Infinity;
  for (let i = 1; i < ys.length; i++) if (ys[i] != null && ys[i - 1] != null) minGap = Math.min(minGap, Math.abs(ys[i - 1]! - ys[i]!));
  const compact = minGap < 54 || w0 < 420;
  hs.forEach((h, i) => {
    const p = pts[i];
    if (!p) return;
    h.classList.toggle('is-compact', compact);
    let x: number;
    if (compact) {
      const below = Math.abs((ys[i] ?? p.y) - p.y), above = Math.abs(p.y - (ys[i + 2] ?? p.y));
      const hh = Math.max(18, Math.min(56, Math.min(below, above) - 2));
      h.style.height = `${hh.toFixed(1)}px`;
      h.style.setProperty('--hh', `${hh.toFixed(1)}px`);
      x = p.x - 44 - 4;
      h.classList.remove('is-inside');
    } else {
      h.style.height = '';
      const w = h.offsetWidth || 44;
      const inside = w0 - p.x - 12 < w + 10;
      x = inside ? p.x - w - 6 : p.x + 10;
      h.classList.toggle('is-inside', inside);
    }
    h.style.transform = `translate(${x.toFixed(1)}px, ${p.y.toFixed(1)}px) translateY(-50%)`;
    h.style.visibility = p.visible ? 'visible' : 'hidden';
  });
}
let offFrame: (() => void) | null = null;
onMounted(() => {
  offFrame = viewerRef.value?.onFrame(placeHandles) ?? null;
  requestAnimationFrame(placeHandles);
});
onBeforeUnmount(() => {
  offFrame?.();
  if (keyTimer) { clearTimeout(keyTimer); keyTimer = null; commitPreview('Shelf moved'); }
  if (stagePreview.value) { stagePreview.value = null; viewerRef.value?.update(app.store.build, { animate: false }); }
});

function mmPerPx(): number {
  const viewer = viewerRef.value!;
  const b = build.value;
  const a = viewer.getScreenPosition({ point: { x: b.envelope.W, y: 0, z: b.envelope.D } });
  const c = viewer.getScreenPosition({ point: { x: b.envelope.W, y: b.envelope.H, z: b.envelope.D } });
  const px = (a?.y ?? 0) - (c?.y ?? 0);
  return px > 1 ? b.envelope.H / px : 1;
}

// ---------------------------------------------------------------------------------------------
// Drag
// ---------------------------------------------------------------------------------------------
let drag: { h: HTMLElement; k: number; y0: number; datum0: number; scale: number; base: Design; moved: boolean; pointerId: number } | null = null;
function onDown(e: PointerEvent, k: number) {
  if (e.button !== 0) return;
  e.preventDefault();
  const h = e.currentTarget as HTMLElement;
  const part = app.store.build.parts.find((p) => p.id === partIdOf(k));
  if (!part || part.datum == null) return;
  drag = { h, k, y0: e.clientY, datum0: part.datum, scale: mmPerPx(), base: app.store.design, moved: false, pointerId: e.pointerId };
  h.setPointerCapture(e.pointerId);
  h.classList.add('is-drag');
  emit('dragging', true);
  viewerRef.value?.select(partIdOf(k));
}
function onMove(e: PointerEvent) {
  if (!drag) return;
  const dy = drag.y0 - e.clientY;
  if (Math.abs(dy) > 2) drag.moved = true;
  const datum = Math.round(drag.datum0 + dy * drag.scale);
  previewDesign(setShelfDatum(drag.base, drag.k, mm(datum), { construction: app.store.construction, bay: bay.value }));
}
function onUp() {
  if (!drag) return;
  const { h, k, moved } = drag;
  h.classList.remove('is-drag');
  emit('dragging', false);
  drag = null;
  if (moved) commitPreview(shelfMoved(k));
  else {
    if (stagePreview.value) { stagePreview.value = null; viewerRef.value?.update(app.store.build, { animate: false }); }
    viewerRef.value?.select(ui.sel);
    h.focus();
  }
}

// ---------------------------------------------------------------------------------------------
// Keyboard slider
// ---------------------------------------------------------------------------------------------
let keyTimer: ReturnType<typeof setTimeout> | null = null;
function onKey(e: KeyboardEvent, k: number) {
  const step = ({ ArrowUp: 1, ArrowDown: -1, PageUp: 10, PageDown: -10 } as Record<string, number>)[e.key];
  if (!step) return;
  e.preventDefault();
  const base = stagePreview.value?.design ?? app.store.design;
  const b = stagePreview.value?.build ?? app.store.build;
  const datum = b.parts.find((p) => p.id === partIdOf(k))?.datum;
  if (datum == null) return;
  previewDesign(setShelfDatum(base, k, mm(datum + step * (e.shiftKey ? 10 : 1)), { construction: app.store.construction, bay: bay.value }));
  if (keyTimer) clearTimeout(keyTimer);
  keyTimer = setTimeout(() => { keyTimer = null; commitPreview(shelfMoved(k)); }, 650);
}
function onBlur(k: number) {
  if (keyTimer) { clearTimeout(keyTimer); keyTimer = null; commitPreview(shelfMoved(k)); }
}
function shelfMoved(k: number): string { return multi.value ? `Bay ${bay.value + 1} shelf ${k} moved` : `Shelf ${k} moved`; }
function onFocus(k: number) {
  viewerRef.value?.select(partIdOf(k));
  if (ui.sel) ui.sel = null;
  if (window.innerWidth <= 1000) props.stage?.scrollIntoView({ block: 'nearest' });
}
</script>

<template>
  <div id="handles" ref="wrap" class="handles">
    <button
      v-for="(h, i) in info"
      :key="h.k"
      :ref="(el) => setEl(el, i)"
      type="button"
      class="handle"
      role="slider"
      :data-shelf="h.k"
      aria-orientation="vertical"
      :aria-label="h.label"
      :aria-valuemin="h.min"
      :aria-valuemax="h.max"
      :aria-valuenow="h.now"
      :aria-valuetext="h.text"
      @pointerdown="onDown($event, h.k)"
      @pointermove="onMove"
      @pointerup="onUp"
      @pointercancel="onUp"
      @keydown="onKey($event, h.k)"
      @blur="onBlur(h.k)"
      @focus="onFocus(h.k)"
    >
      <span class="h-grip"><Icon name="grip" /></span><span class="h-label">{{ multi ? `B${bay + 1} · ${h.k}` : `Shelf ${h.k}` }}</span><span class="h-val mono">{{ h.value }}</span>
    </button>
  </div>
</template>
